import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import ModalPortal from "./ModalPortal";

// Actor names are enriched server-side by each v2 service (Allergy/FH/…).
// The `*ByName` fields on each row are the authoritative display value;
// this fallback only runs on a resolver miss or an older payload.
const actorLabel = (byName, actor) => {
  if (byName && byName.trim()) return byName.trim();
  if (!actor) return "";
  if (typeof actor === "string") return actor.trim();
  if (actor.who === "service") return "System";
  const label = actor.who ? actor.who[0].toUpperCase() + actor.who.slice(1) : "";
  if (label && actor.whoId) return `${label} · ${actor.whoId}`;
  return label || "";
};

/**
 * "Deleted entries" — the shared audit dialog, px-matched to Figma 4065:3098
 * ("Popup · Deleted entries — Family history", 560x396).
 *
 * Family history and Allergies used to keep two copies of this markup that
 * merely agreed to reuse the same .fh3-del-* CSS. They drifted the moment the
 * board landed: the restyle moved the in-card divider from a border on the
 * audit row to its own node, and only one of the two copies was updated. One
 * component, two callers.
 *
 * The shell is fixed (header, one card per entry, the red "Deleted" pill, the
 * ADDED/DELETED audit row, the footer). A section supplies only its subtitle,
 * a key, a per-entry title, and the label/value rows in the middle.
 */

// "13 Jan 2026, 3:02 pm" — the board's audit format. Note it does NOT
// zero-pad the hour: two of the board's six sample timestamps do and four do
// not, so the padding is mock drift rather than a rule.
const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  return `${date}, ${time}`;
};

/** Joins `[{name}]` or `["a","b"]` into "a, b". Both shapes occur on the wire. */
export const joinNames = (list) => {
  if (!Array.isArray(list) || list.length === 0) return "—";
  const out = list
    .map((x) => (typeof x === "string" ? x.trim() : (x && x.name) || ""))
    .filter(Boolean)
    .join(", ");
  return out || "—";
};

/* The board issues this as a real asset: a 14x14 X inset 7 in a 28 box,
   1.5 stroke, round caps. The 28x28 bordered box is the button, drawn in CSS. */
const CloseIcon = () => (
  <svg viewBox="0 0 28 28" width="28" height="28" fill="none" aria-hidden="true">
    <path
      d="M7 7L21 21M21 7L7 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const DeletedEntriesDialog = ({
  patient,
  entries,
  subtitle,
  titleId,
  getKey,
  getTitle,
  getFields,
  emptyText,
  onClose,
}) => {
  // Actor names are enriched by the backend on every list/mutation
  // response — no roster fetch needed here.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Most-recently-deleted first (per product spec). Falls back to the
  // supplied order for rows without a deletedAt so a malformed row
  // never disappears; NaN-guarded to keep sort stable.
  const rows = useMemo(() => {
    const list = entries || [];
    const ts = (d) => {
      if (!d) return -Infinity;
      const t = new Date(d).getTime();
      return isNaN(t) ? -Infinity : t;
    };
    return [...list].sort((a, b) => ts(b.deletedAt) - ts(a.deletedAt));
  }, [entries]);

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal fh3-del-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <header className="fh3-del-head">
            <h2 id={titleId} className="fh3-del-title">
              Deleted entries
            </h2>
            {/* A sibling of the title on a 12px gap, not a nested span, so there
                is no literal leading space doing the spacing. */}
            <span className="fh3-del-sub">&middot; {subtitle}</span>
            <button
              type="button"
              className="fh3-del-close"
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </header>
  
          <div className="fh3-del-body">
            {rows.length === 0 ? (
              <p className="fh3-del-empty">{emptyText}</p>
            ) : (
              rows.map((r) => (
                <article className="fh3-del-card" key={getKey(r)}>
                  <div className="fh3-del-card-head">
                    <h3 className="fh3-del-card-title">{getTitle(r)}</h3>
                    <span className="fh3-del-pill">Deleted</span>
                  </div>
  
                  <dl className="fh3-del-dl">
                    {getFields(r).map(([label, value]) => (
                      <React.Fragment key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
  
                  {/* A real 1px node on the board, not a border on the row. */}
                  <hr className="fh3-del-rule" />
  
                  <div className="fh3-del-audit">
                    <div className="fh3-del-audit-cell">
                      <span className="fh3-del-audit-label">ADDED</span>
                      <span className="fh3-del-audit-value">
                        {actorLabel(r.addedByName, r.addedBy) || "—"}
                        {r.addedAt ? ` · ${formatDateTime(r.addedAt)}` : ""}
                      </span>
                    </div>
                    <div className="fh3-del-audit-cell is-danger">
                      <span className="fh3-del-audit-label">DELETED</span>
                      <span className="fh3-del-audit-value">
                        {actorLabel(r.deletedByName, r.deletedBy) || "—"}
                        {r.deletedAt ? ` · ${formatDateTime(r.deletedAt)}` : ""}
                      </span>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
  
          <div className="fh3-del-foot">
            <button
              type="button"
              className="sec-btn sec-btn-ghost fh3-del-close-btn"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

DeletedEntriesDialog.propTypes = {
  patient: PropTypes.object,
  entries: PropTypes.array,
  /** Follows the "· " middot in the header, e.g. "Family history". */
  subtitle: PropTypes.string.isRequired,
  /** Unique per section — two dialogs never coexist, but ids must not clash. */
  titleId: PropTypes.string.isRequired,
  getKey: PropTypes.func.isRequired,
  getTitle: PropTypes.func.isRequired,
  /** (entry) => [[label, value], …] rendered as the card's definition list. */
  getFields: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

DeletedEntriesDialog.defaultProps = {
  emptyText: "No deleted entries yet.",
};

export default DeletedEntriesDialog;
